import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../lib/AuthContext';

const SESSION_KEY = 'pubsilo_hub_buy_params';

export default function HubBuyPage() {
  const navigate = useNavigate();

  const params    = new URLSearchParams(window.location.search);
  const articleId = params.get('article');
  const company   = params.get('company');
  const titleHint = params.get('title') || 'Article';
  const priceHint = params.get('price');

  // Persist params across the login redirect so they survive the navigation
  useEffect(() => {
    if (articleId) {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify({ article: articleId, company, title: titleHint, price: priceHint }));
    }
  }, [articleId, company, titleHint, priceHint]);

  const saved = (() => { try { return JSON.parse(sessionStorage.getItem(SESSION_KEY) || 'null'); } catch { return null; } })();
  const eid    = articleId || saved?.article;
  const ecomp  = company   || saved?.company;
  const etitle = titleHint || saved?.title || 'Article';
  const eprice = priceHint || saved?.price;

  const { accessToken: token } = useAuth();
  const userType   = localStorage.getItem('userType');
  const isLoggedIn = !!token && userType === 'user';

  const [articleData, setArticleData] = useState(null);
  const [livePrice, setLivePrice]     = useState(null);
  const [user, setUser]               = useState(null);
  const [phase, setPhase]             = useState('loading');
  const [msg, setMsg]                 = useState('');

  // Always fetch the live price from the public endpoint so the correct price
  // shows even before login, regardless of what price= is in the URL.
  useEffect(() => {
    if (!eid) return;
    axios.get(`/api/embed/price/${encodeURIComponent(eid)}`).then(res => setLivePrice(res.data)).catch(() => {});
  }, [eid]);

  const goToArticle = () => {
    sessionStorage.removeItem(SESSION_KEY);
    navigate(`/${encodeURIComponent(ecomp)}?a=${eid}&read=1`);
  };

  useEffect(() => {
    if (!eid || !ecomp) {
      setPhase('error');
      setMsg('Missing article or company information.');
      return;
    }
    if (!isLoggedIn) {
      setPhase('auth');
      return;
    }

    const headers = { Authorization: `Bearer ${token}` };

    Promise.all([
      axios.get(`/api/embed/price/${encodeURIComponent(eid)}`),
      axios.get('/api/users/me', { headers }),
    ])
      .then(([artRes, userRes]) => {
        const art = artRes.data;
        const usr = userRes.data;
        setArticleData(art);
        setUser(usr);

        const alreadyOwned = (usr.purchased || []).some(
          p => String(p.articleId) === String(eid) && !p.returned
        );
        if (alreadyOwned) { goToArticle(); return; }

        const balance    = usr.tokens || 0;
        const cost       = art.token_cost;
        const shortfall  = cost - balance;
        const needsPromo = balance > 0 && shortfall > 0 && shortfall <= 15 && (usr.promoTokens || 0) === 0;

        if (balance >= cost || needsPromo) {
          setPhase(needsPromo ? 'promo' : 'confirm');
        } else {
          setPhase('buying');
        }
      })
      .catch(err => {
        setPhase('error');
        setMsg(err.response?.data?.error || 'Failed to load article info.');
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  async function handlePurchase() {
    setPhase('loading');
    try {
      await axios.post(`/api/users/me/purchase/${eid}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      goToArticle();
    } catch (err) {
      setPhase('error');
      setMsg(err.response?.data?.error || 'Purchase failed. Please try again.');
    }
  }

  const hubParams   = new URLSearchParams({ article: eid || '', company: ecomp || '', title: etitle, price: eprice || '' });
  const loginHref   = `/login?next=${encodeURIComponent(`/hub-buy?${hubParams}`)}`;
  const signupHref  = `/signup/reader?next=${encodeURIComponent(`/hub-buy?${hubParams}`)}`;
  const tokenHref   = `/tokens/purchase?next=${encodeURIComponent(`/hub-buy?${hubParams}`)}`;
  const companyHref = ecomp ? `/${encodeURIComponent(ecomp)}` : '/';

  return (
    <div style={{
      minHeight: '100vh', backgroundColor: 'var(--pubsilo-bg)',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', padding: '2rem 1rem',
    }}>
      <div style={{
        background: '#fff', borderRadius: '14px', padding: '2.5rem 2rem',
        maxWidth: '440px', width: '100%',
        boxShadow: '0 12px 40px rgba(13,49,101,0.15)',
      }}>
        <img src="/pubsilo.png" alt="PubSilo" style={{ height: 56, objectFit: 'contain', display: 'block', margin: '0 auto 1.5rem' }} />

        {phase === 'loading' && (
          <p style={{ textAlign: 'center', color: 'var(--pubsilo-muted)' }}>Loading…</p>
        )}

        {phase === 'error' && (
          <>
            <h2 style={hd}>Something went wrong</h2>
            <p style={sub}>{msg}</p>
            <a href={companyHref} style={btnPrimary}>← Return to publisher page</a>
          </>
        )}

        {phase === 'auth' && (
          <>
            <h2 style={hd}>Access this article on PubSilo</h2>
            <p style={sub}>Sign in or create a free account to purchase and read.</p>
            {etitle && (
              <p style={{ ...sub, fontStyle: 'italic', marginBottom: '1.25rem' }}>
                "{etitle}"
              </p>
            )}
            {(livePrice || eprice) && (
              <p style={{ ...sub, marginBottom: '1.5rem' }}>
                Price: <strong>
                  {livePrice
                    ? `${livePrice.token_cost} credit${livePrice.token_cost !== 1 ? 's' : ''}`
                    : `${eprice} credit${eprice !== '1' ? 's' : ''}`}
                </strong>
              </p>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '1.5rem' }}>
              <a href={loginHref} style={btnPrimary}>Sign In</a>
              <a href={signupHref} style={btnSecondary}>Create Free Account</a>
            </div>
            <a href={companyHref} style={backLink}>← Browse without purchasing</a>
          </>
        )}

        {(phase === 'confirm' || phase === 'promo') && articleData && user && (
          <>
            <div style={{ marginBottom: '1.5rem' }}>
              <h2 style={{ ...hd, marginBottom: '0.35rem' }}>{articleData.title || etitle}</h2>
              {ecomp && (
                <p style={{ ...sub, marginBottom: 0 }}>Published by <strong>{ecomp}</strong></p>
              )}
            </div>

            <div style={{
              background: 'var(--pubsilo-light)', borderRadius: '9px',
              padding: '1rem 1.25rem', marginBottom: '1.5rem',
              border: '1px solid var(--pubsilo-border)',
            }}>
              <Row label="Article price" value={`${articleData.token_cost} credit${articleData.token_cost !== 1 ? 's' : ''}`} />
              <Row label="Your balance"  value={`${user.tokens} credit${user.tokens !== 1 ? 's' : ''}`} />
              {phase === 'confirm' && (
                <Row label="Balance after purchase" value={`${user.tokens - articleData.token_cost} credits`} highlight />
              )}
              {phase === 'promo' && (
                <Row label="Promo credits gifted" value={`${articleData.token_cost - user.tokens} credit${(articleData.token_cost - user.tokens) !== 1 ? 's' : ''}`} highlight />
              )}
              {phase === 'promo' && (
                <Row label="Balance after purchase" value="0 credits" highlight warn />
              )}
            </div>

            {phase === 'promo' && (
              <p style={{ fontSize: '0.85rem', color: '#1a5e9a', marginBottom: '1rem', background: '#eef4ff', borderRadius: '7px', padding: '0.75rem 1rem', border: '1px solid #b3d0f5' }}>
                You're {articleData.token_cost - user.tokens} credit{articleData.token_cost - user.tokens !== 1 ? 's' : ''} short. We'll gift you the difference so you can read now.
              </p>
            )}

            <button onClick={handlePurchase} style={btnPrimaryBtn}>
              {phase === 'promo'
                ? `Purchase · ${articleData.token_cost} credits · ${articleData.token_cost - user.tokens} gifted`
                : `Purchase · ${articleData.token_cost} credits`}
            </button>

            {phase === 'promo' && (
              <button onClick={() => navigate(tokenHref)} style={{ ...btnSecondaryBtn, marginTop: '0.5rem' }}>
                Buy More Article Credits Instead
              </button>
            )}

            <a href={companyHref} style={backLink}>← Browse without purchasing</a>
          </>
        )}

        {phase === 'buying' && articleData && user && (
          <>
            <h2 style={hd}>Not enough Article Credits</h2>
            <div style={{
              background: 'var(--pubsilo-light)', borderRadius: '9px',
              padding: '1rem 1.25rem', marginBottom: '1.5rem',
              border: '1px solid var(--pubsilo-border)',
            }}>
              <Row label="Article price" value={`${articleData.token_cost} credits`} />
              <Row label="Your balance"  value={`${user.tokens} credits`} />
              <Row label="You need"      value={`${articleData.token_cost - user.tokens} more credits`} highlight warn />
            </div>
            <p style={{ fontSize: '0.88rem', color: 'var(--pubsilo-muted)', marginBottom: '1.25rem' }}>
              Purchase Article Credits to unlock <strong>{articleData.title || etitle}</strong>.
            </p>
            <button onClick={() => navigate(tokenHref)} style={btnPrimaryBtn}>Buy Article Credits</button>
            <a href={companyHref} style={backLink}>← Browse without purchasing</a>
          </>
        )}
      </div>
    </div>
  );
}

function Row({ label, value, highlight, warn }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.88rem', padding: '0.2rem 0' }}>
      <span style={{ color: 'var(--pubsilo-muted)' }}>{label}</span>
      <span style={{ fontWeight: 700, color: warn ? '#b06000' : highlight ? 'var(--pubsilo-blue)' : 'var(--pubsilo-text)' }}>{value}</span>
    </div>
  );
}

const hd = { fontSize: '1.3rem', fontWeight: 800, color: 'var(--pubsilo-blue-dark)', marginBottom: '0.5rem', textAlign: 'center' };
const sub = { fontSize: '0.9rem', color: 'var(--pubsilo-muted)', textAlign: 'center', marginBottom: '0.5rem' };
const btnPrimary = {
  display: 'block', width: '100%', padding: '0.75rem', textAlign: 'center',
  backgroundColor: 'var(--pubsilo-blue)', color: '#fff', borderRadius: '7px',
  fontWeight: 700, textDecoration: 'none', fontSize: '0.95rem',
};
const btnSecondary = {
  display: 'block', width: '100%', padding: '0.75rem', textAlign: 'center',
  backgroundColor: 'transparent', color: 'var(--pubsilo-blue)', borderRadius: '7px',
  fontWeight: 700, textDecoration: 'none', fontSize: '0.95rem',
  border: '1.5px solid var(--pubsilo-blue)',
};
const btnPrimaryBtn   = { ...btnPrimary,    border: 'none', cursor: 'pointer', display: 'block', width: '100%' };
const btnSecondaryBtn = { ...btnSecondary,  cursor: 'pointer', background: 'transparent' };
const backLink = {
  display: 'block', textAlign: 'center', marginTop: '1.25rem',
  fontSize: '0.82rem', color: 'var(--pubsilo-muted)', textDecoration: 'none',
};
